import { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2 } from 'lucide-react'
import apiClient from '@/api/client'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useCurrency } from '@/contexts/CurrencyContext'
import { stockItemRefs, useStockItemOptions } from '@/hooks/useStockItemOptions'
import { optionRecipeBody } from '@/lib/costingSheetState'
import {
  collectBlocks,
  fromServerComponents,
  multiplierValid,
  type RecipeLineDraft,
  type StockItemRef,
} from '@/lib/recipeEditor'
import { toastHelpers } from '@/lib/toast-helpers'
import type { OptionRecipe } from '@/types'
import { OptionSwaps, newSwap, swapProblem, type SwapDraft } from './OptionSwaps'
import { RecipeEditor } from './RecipeEditor'
import { invalidateAfterRecipeSave, saveIngredientCost } from './costingQueries'

/**
 * An option's own recipe, edited from the Modifiers tab (F-MOD-N11).
 *
 * Saved on its own — the option's name and price go through the option
 * dialog's Save, the ingredients through this panel — so the two never block
 * each other. Shared by every product that carries the option's group, so a
 * save that changes more than one item asks first.
 */
export function OptionIngredients({
  optionId,
  optionName,
  onDirtyChange,
}: {
  optionId: string
  optionName: string
  /** Told whenever the unsaved state flips, so the option dialog can ask before closing (W-2). */
  onDirtyChange?: (dirty: boolean) => void
}) {
  const qc = useQueryClient()
  const { formatCurrency } = useCurrency()
  const { data: stockOptions } = useStockItemOptions()
  const items = useMemo(() => stockItemRefs(stockOptions), [stockOptions])
  const itemList = useMemo(() => Object.values(items).sort((a, b) => a.name.localeCompare(b.name)), [items])

  const q = useQuery({
    queryKey: ['option-recipe', optionId],
    queryFn: () => apiClient.getOptionRecipe(optionId).then((r) => r.data as OptionRecipe),
  })
  const [lines, setLines] = useState<RecipeLineDraft[]>([])
  const [mult, setMult] = useState('1')
  const [swaps, setSwaps] = useState<SwapDraft[]>([])
  const [baseline, setBaseline] = useState('')
  const [appliedSig, setAppliedSig] = useState<string | null>(null)
  const [stale, setStale] = useState(false)
  const [saving, setSaving] = useState(false)
  const [confirmShared, setConfirmShared] = useState(false)
  const expecting = useRef<'adopt' | 'rebase' | null>(null)

  const snapshot = (ls: RecipeLineDraft[], m: string, sw: SwapDraft[]) =>
    JSON.stringify({ l: ls.map(strip), m: m.trim(), s: sw.filter((x) => x.from || x.to).map((x) => [x.from, x.to]) })
  const swapsFrom = (data: OptionRecipe) => (data.swaps ?? []).map((x) => newSwap(x.from_stock_item_id, x.to_stock_item_id))
  const dirty = appliedSig != null && snapshot(lines, mult, swaps) !== baseline
  const dirtyRef = useRef(dirty)
  dirtyRef.current = dirty
  useEffect(() => {
    onDirtyChange?.(dirty)
  }, [dirty, onDirtyChange])
  useEffect(() => () => onDirtyChange?.(false), [onDirtyChange])

  const apply = (data: OptionRecipe) => {
    const l = fromServerComponents(data.components, 1)
    const m = String(data.recipe_multiplier || 1)
    const sw = swapsFrom(data)
    setLines(l)
    setMult(m)
    setSwaps(sw)
    setBaseline(snapshot(l, m, sw))
    setAppliedSig(JSON.stringify(data))
    setStale(false)
  }

  // Never replace what the owner is typing (audit U-2): a refetch with news
  // while the draft is dirty shows a banner instead.
  useEffect(() => {
    if (!q.data) return
    const sig = JSON.stringify(q.data)
    if (appliedSig == null) return apply(q.data)
    if (sig === appliedSig) return
    const exp = expecting.current
    expecting.current = null
    if (exp === 'rebase') {
      setBaseline(snapshot(fromServerComponents(q.data.components, 1), String(q.data.recipe_multiplier || 1), swapsFrom(q.data)))
      setAppliedSig(sig)
      return
    }
    if (exp === 'adopt' || !dirtyRef.current) return apply(q.data)
    setStale(true)
    setAppliedSig(sig)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q.data])

  const cap = q.data?.max_item_price ?? 0
  const productCount = q.data?.product_count ?? 0
  const blocks = collectBlocks(lines, items, cap, 1, formatCurrency, 'option')
  const swapsSupported = q.data?.swaps_supported === true
  const swapProblems = swaps.filter((x) => swapProblem(x, items, swaps) != null).length
  const reason = saving
    ? 'Saving…'
    : !dirty
      ? ''
      : blocks.length > 0
        ? `Fix ${blocks.length} highlighted line${blocks.length === 1 ? '' : 's'}`
        : !multiplierValid(mult)
          ? 'The multiplier must be above 0'
          : swapProblems > 0
            ? `Fix ${swapProblems} swap${swapProblems === 1 ? '' : 's'}`
            : null

  const doSave = async () => {
    setSaving(true)
    try {
      const body = optionRecipeBody({ multiplier: mult, lines })
      await apiClient.putOptionRecipe(
        optionId,
        swapsSupported
          ? { ...body, swaps: swaps.filter((x) => x.from && x.to).map((x) => ({ from_stock_item_id: x.from, to_stock_item_id: x.to })) }
          : body,
      )
      expecting.current = 'adopt'
      toastHelpers.success('Ingredients saved', `${optionName} now deducts its own recipe.`)
    } catch (e) {
      toastHelpers.apiError('Save ingredients', e)
    } finally {
      setSaving(false)
      invalidateAfterRecipeSave(qc)
    }
  }

  const saveCost = (item: StockItemRef, cost: number): Promise<boolean> =>
    saveIngredientCost(qc, item, cost, formatCurrency, () => {
      expecting.current = 'rebase'
    })

  return (
    <div className="space-y-3 rounded-md border p-3">
      <div>
        <p className="text-sm font-medium">Ingredients</p>
        <p className="text-xs text-muted-foreground">
          What this option adds to or replaces in the item&apos;s recipe.{' '}
          {productCount > 1 ? `Shared by ${productCount} items that use this group.` : 'Shared by every item that uses this group.'}{' '}
          Leave empty and the option changes nothing about stock.
        </p>
      </div>
      {stale && (
        <div className="flex flex-wrap items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-2.5 py-1.5 text-xs text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-300">
          <span className="flex-1">This option&apos;s recipe changed elsewhere.</span>
          <Button type="button" size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => q.data && apply(q.data)}>
            Reload
          </Button>
        </div>
      )}
      {q.isLoading ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…</p>
      ) : (
        <>
          <div className="flex flex-wrap items-center gap-2">
            <Label htmlFor={`opt-mult-${optionId}`} className="text-xs">Makes</Label>
            <Input id={`opt-mult-${optionId}`} type="number" min={0} step="any" inputMode="decimal" className="h-8 w-20 tabular-nums" value={mult} onChange={(e) => setMult(e.target.value)} aria-invalid={!multiplierValid(mult)} />
            <span className="text-xs text-muted-foreground">× the base recipe (Whole Cake on a sliced cake = 8; leave 1 otherwise)</span>
          </div>
          <RecipeEditor
            kind="option"
            lines={lines}
            onChange={setLines}
            items={items}
            itemList={itemList}
            price={cap}
            yieldN={1}
            formatMoney={formatCurrency}
            onSaveCost={saveCost}
          />
          {swapsSupported && (
            <OptionSwaps swaps={swaps} onChange={setSwaps} items={items} itemList={itemList} formatMoney={formatCurrency} />
          )}
          <div className="flex items-center justify-end gap-2">
            <span className="flex-1 text-xs text-muted-foreground" aria-live="polite">{reason ?? 'Unsaved ingredient changes'}</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={reason !== null}
              onClick={() => (productCount > 1 ? setConfirmShared(true) : void doSave())}
            >
              {saving ? 'Saving…' : 'Save ingredients'}
            </Button>
          </div>
        </>
      )}

      <Dialog open={confirmShared} onOpenChange={setConfirmShared}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Change {optionName} for {productCount} items?</DialogTitle>
            <DialogDescription>
              This option&apos;s recipe is shared by all {productCount} items that use its group. Saving changes what each of them deducts.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setConfirmShared(false)}>Keep editing</Button>
            <Button
              type="button"
              onClick={() => {
                setConfirmShared(false)
                void doSave()
              }}
            >
              Save for all {productCount}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function strip(l: RecipeLineDraft) {
  const { key: _k, ...rest } = l
  void _k
  return rest
}
